import { groq } from "next-sanity"

import { AlgoliaSearch } from "@/services/algoliaSearch"
import { CommerceService } from "@/services/commerce"
import { AppSettings } from "@/settings"

import { isIdmUser } from "../../shared/permissions/permissions"
import { getExcludedCustomerGroups, getExcludedIndustryKeys, noDataReturn } from "../utils/customerQueryHelpers"

const commerceApi = CommerceService.api()
const algoliaIndexName = "customers"

export const queryCustomersListCheck = {
  async customersListCheck(_parent, args, context, _info) {
    const { offset, limit, page, searchTerm } = args
    const { user } = context

    if (isIdmUser(user?.role)) {
      throw new Error("Unauthorized")
    }

    let foundCustomers = []

    if (searchTerm && searchTerm.length > 0) {
      try {
        foundCustomers = await searchAlgoliaCustomers({ page, searchTerm, limit })
      } catch (err) {
        console.warn("Error searching customers in algolia", err)
        foundCustomers = await getCtCustomers({ offset, limit })
      }
    } else {
      foundCustomers = await getCtCustomers({ offset, limit })
    }

    if (foundCustomers?.length < 1) {
      return noDataReturn(page, limit)
    }

    const admRepNumbers = foundCustomers.items
      .map((customer) => (customer?.admSalesRepresentive ? `"${customer?.admSalesRepresentive}"` : null))
      .filter((n) => n)

    const branchOfficeKeys = foundCustomers.items
      .map((customer) => (customer?.customerBranch ? `"${customer?.customerBranch}"` : null))
      .filter((n) => n)

    const defaultReturn = {
      page,
      limit,
      count: foundCustomers.count,
      total: foundCustomers.total,
      items: foundCustomers.items,
    }

    if (admRepNumbers.length < 1) {
      return defaultReturn
    }

    const config = AppSettings.sanityConfigByLang()
    const client = context.sanityClient
    client.config({ ...config, useCdn: true })

    const sanitData = await client.fetch(
      groq`{
        "branchOffices": *[_type=="branchOffice" && id in [${branchOfficeKeys}]] {
          id,
          title,
        },
        "admRepData": *[_type=="employee" && key in [${admRepNumbers}]] {
          key,
          name,
        }
      }`,
    )

    if (!sanitData?.admRepData || sanitData?.admRepData.length < 1) {
      return defaultReturn
    }

    return {
      ...defaultReturn,
      items: foundCustomers.items.map((customer) => {
        const admRep = sanitData?.admRepData.find((rep) => rep?.key === customer?.admSalesRepresentive)

        let branchTitle = customer?.branchTitle || null
        const customerBranch = customer?.customerBranch || null

        if (customerBranch && !branchTitle) {
          const branch = sanitData?.branchOffices.find((branch) => branch?.id === customerBranch)
          branchTitle = branch?.title || null
        }

        return {
          ...customer,
          id: customer?.objectID || customer?.id,
          admRepresentative: admRep?.name || customer?.admSalesRepresentive,
          branchTitle,
        }
      }),
    }
  },
}

const getCtCustomers = async ({ offset, limit }) => {
  const excludedCustomerGroups = await getExcludedCustomerGroups()
  const excludedIndustryKeys = await getExcludedIndustryKeys()

  const customers = (
    await commerceApi
      .customers()
      .get({
        queryArgs: {
          limit: limit,
          offset: offset,
          where: `customerGroup(id not in (${excludedCustomerGroups})) and custom(fields(industryKey(id is defined and id not in (${excludedIndustryKeys}))))`,
        },
      })
      .execute()
  )?.body

  if (!customers || customers?.results?.length === 0) {
    return noDataReturn(0, limit)
  }

  return {
    items: mapCustomerData(customers?.results),
    limit,
    offset,
    count: customers?.count,
    total: customers?.total,
  }
}

const searchAlgoliaCustomers = async ({ page, searchTerm, limit }) => {
  const algoliaClient = AlgoliaSearch.client()

  const hasCustomers = await algoliaClient.searchSingleIndex({
    indexName: algoliaIndexName,
    searchParams: { query: "" },
  })

  if (!hasCustomers || hasCustomers?.hits?.length < 1) {
    throw new Error("No customers found in algolia")
  }

  const usedPage = page ? Number(page) - 1 : 0
  const customersResponse = await algoliaClient.searchSingleIndex({
    indexName: algoliaIndexName,
    searchParams: {
      query: searchTerm,
      page: usedPage,
      clickAnalytics: true,
      hitsPerPage: limit,
    },
  })

  if (!customersResponse || customersResponse?.hits?.length === 0) {
    return noDataReturn(page, limit)
  }

  return {
    items: customersResponse?.hits?.map((customer) => {
      return {
        ...customer,
        id: customer?.objectID,
      }
    }),
    limit,
    count: customersResponse?.hits?.length,
    total: customersResponse?.nbHits,
  }
}

/**
 * Returns mapped customer data
 *
 * @param {array} customers
 *
 * @returns {Array}
 */
const mapCustomerData = (customers) => {
  return customers.map((customer) => {
    const customerBillingAddress = customer?.addresses?.find(
      (address) => address?.id === customer?.defaultBillingAddressId,
    )

    return {
      id: customer.id,
      customerNumber: customer.customerNumber,
      companyName: customer.companyName,
      matchCode: customer?.custom?.fields?.matchCode,
      customerBranch: customer?.custom.fields.deliveryBranch || customer?.custom?.fields?.customerBranch,
      city: customerBillingAddress?.city,
      streetName: customerBillingAddress?.streetName,
      postalCode: customerBillingAddress?.postalCode,
      customerClassification: customer?.custom?.fields?.customerClassification,
      admSalesRepresentive: customer?.custom?.fields?.admSalesRepresentive,
    }
  })
}
